import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { ProgressBar } from "@/components/ProgressBar";
import { Search, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTeachingCourses, getCourseStudents, type Course, type EnrolledStudentRow } from "@/api/lms/courses";

type StudentRow = EnrolledStudentRow & { courseTitle: string; courseId: string };

function displayStudentName(s: EnrolledStudentRow): string {
  const fn = s.user.profile?.firstName ?? "";
  const ln = s.user.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || s.user.email;
}

function progressPercentageToGrade(progress: number): string {
  if (!Number.isFinite(progress)) return "-";
  const p = Math.max(0, Math.min(100, progress));
  if (p >= 90) return "A+";
  if (p >= 80) return "A";
  if (p >= 75) return "A-";
  if (p >= 70) return "B+";
  if (p >= 60) return "B";
  if (p >= 50) return "C+";
  if (p >= 40) return "C";
  if (p >= 30) return "D";
  return "F";
}

function formatLastActive(value?: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "-";

  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.round(diffHr / 24);
  return `${diffDays}d ago`;
}

export default function TeacherStudentsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teaching, setTeaching] = useState<Course[]>([]);
  const [studentsByCourse, setStudentsByCourse] = useState<Record<string, EnrolledStudentRow[]>>({});
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const courses = await getTeachingCourses();
        if (cancelled) return;
        setTeaching(courses);

        const entries = await Promise.all(
          courses.map(async (c) => {
            try {
              const list = await getCourseStudents(c.id);
              return [c.id, list] as [string, EnrolledStudentRow[]];
            } catch {
              return [c.id, []] as [string, EnrolledStudentRow[]];
            }
          }),
        );
        if (cancelled) return;

        const map: Record<string, EnrolledStudentRow[]> = {};
        entries.forEach(([id, list]) => {
          map[id] = list;
        });
        setStudentsByCourse(map);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load students");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const rows: StudentRow[] = useMemo(() => {
    const flat: StudentRow[] = [];
    teaching.forEach((c) => {
      (studentsByCourse[c.id] ?? []).forEach((s) => {
        flat.push({ ...s, courseTitle: c.title, courseId: c.id });
      });
    });
    flat.sort((a, b) => (b.enrolledAt ?? "").localeCompare(a.enrolledAt ?? ""));
    return flat;
  }, [teaching, studentsByCourse]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) => {
      const name = displayStudentName(s).toLowerCase();
      const email = s.user.email.toLowerCase();
      const course = s.courseTitle.toLowerCase();
      return name.includes(q) || email.includes(q) || course.includes(q);
    });
  }, [rows, search]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dash-page">
          <div className="dash-empty-state mt-20">
            <Loader2 className="animate-spin mb-2" size={24} />
            <span className="text-sm font-medium">Loading students…</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Students</h1>
            <p className="dash-page-subtitle">View and manage your students across courses.</p>
          </div>
        </div>
        {error ? (
          <div className="mb-4 text-sm text-destructive">{error}</div>
        ) : null}

        <KCard className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search students or courses..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-10 pl-9 pr-4 rounded-lg bg-secondary text-sm outline-none text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dash-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Progress</th>
                  <th>Grade</th>
                  <th>Last Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-4">
                      <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                        <p className="text-sm font-medium">No students found.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => {
                    const pct = Number.parseFloat(s.progressPercentage ?? "0");
                    const progress = Number.isFinite(pct) ? pct : 0;
                    const gradePercent =
                      s.combinedChapterExamPercentage == null
                        ? NaN
                        : Number.parseFloat(s.combinedChapterExamPercentage);
                    const grade = progressPercentageToGrade(gradePercent);
                    const gradeKnown = grade !== "-";
                    const initial = displayStudentName(s).slice(0, 1).toUpperCase();
                    return (
                      <tr key={`${s.courseId}-${s.id}`}>
                        <td>
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[11px] font-bold shrink-0">
                              {initial || "?"}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground leading-tight">{displayStudentName(s)}</p>
                              <p className="text-[11px] text-muted-foreground">{s.user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-xs text-muted-foreground">{s.courseTitle}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <ProgressBar progress={progress} className="w-16" />
                            <span className="text-xs font-semibold text-foreground">{Math.round(progress)}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`dash-badge ${
                            gradeKnown
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {grade}
                          </span>
                        </td>
                        <td className="text-[11px] text-muted-foreground">{formatLastActive(s.lastAccessedAt)}</td>
                        <td>
                          <button
                            onClick={() => navigate(`/teacher/students/${s.userId}/analytics`)}
                            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-lg hover:bg-primary/5"
                          >
                            View Analytics
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </KCard>
      </div>
    </DashboardLayout>
  );
}
